// src/utils/logger.ts

/**
 * Simple log levels from most verbose to least verbose
 * This enum remains exactly the same to maintain compatibility with existing code
 */
export enum LogLevel {
  TRACE = 0,
  DEBUG = 1,
  INFO = 2,
  WARN = 3,
  ERROR = 4,
  SILENT = 5,
}

/**
 * Detect if we're running in a development environment using Office Add-in specific indicators
 * This replaces the problematic process.env checks with reliable browser-based detection
 */
function detectDevelopmentEnvironment(): boolean {
  try {
    // Get current page location details
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    const port = window.location.port;

    // Development indicators specific to Office Add-ins and local development
    const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1";
    const isDevPort = port === "3000" || port === "3001" || port === "8080" || port === "8081";
    const isHttpProtocol = protocol === "http:"; // Development often uses HTTP

    // Office Add-ins in development typically run on localhost
    const isDevelopment = isLocalhost && (isDevPort || isHttpProtocol);

    return isDevelopment;
  } catch (error) {
    // If detection fails, assume production for safety
    console.warn("Could not detect environment, assuming production:", error);
    return false;
  }
}

/**
 * Parse a log level string to LogLevel enum
 * Returns undefined if the string is not a valid log level
 */
function parseLogLevelString(levelStr: string): LogLevel | undefined {
  const normalized = levelStr.toUpperCase().trim();
  const level = LogLevel[normalized as keyof typeof LogLevel];
  return typeof level === "number" ? level : undefined;
}

/**
 * Get the appropriate log level for the current environment
 * Priority: 1) Manual override, 2) Environment variable, 3) Auto-detect
 */
function getLogLevelFromEnv(): LogLevel {
  try {
    // Priority 1: Check for manual override (runtime override via browser console)
    // This takes highest priority so setLogLevel() always works
    if (typeof window !== "undefined" && (window as any).__MANUAL_LOG_LEVEL__ !== undefined) {
      const manualLevel = (window as any).__MANUAL_LOG_LEVEL__;
      return manualLevel;
    }

    // Priority 2: Check environment variable (build-time config)
    if (process.env.REACT_APP_LOG_LEVEL) {
      const envLevel = parseLogLevelString(process.env.REACT_APP_LOG_LEVEL);
      if (envLevel !== undefined) {
        return envLevel;
      }
      console.warn(
        `Invalid REACT_APP_LOG_LEVEL: "${process.env.REACT_APP_LOG_LEVEL}". ` +
          `Valid values: TRACE, DEBUG, INFO, WARN, ERROR, SILENT. Using auto-detect.`
      );
    }

    // Priority 3: Use environment detection to choose appropriate default level
    const isDevelopment = detectDevelopmentEnvironment();

    if (isDevelopment) {
      // In development, show INFO level and above for good visibility
      return LogLevel.INFO;
    } else {
      // In production, only show warnings and errors to keep console clean
      return LogLevel.WARN;
    }
  } catch (error) {
    // If anything goes wrong, use a safe default
    console.warn("Error determining log level, using WARN as fallback:", error);
    return LogLevel.WARN;
  }
}

/**
 * Simple Logger class that respects log levels
 * This class remains exactly the same to maintain compatibility with existing code
 */
class Logger {
  private currentLevel: LogLevel;
  private prefix: string;

  constructor(prefix: string = "") {
    this.currentLevel = getLogLevelFromEnv();
    this.prefix = prefix;
  }

  /**
   * Check if we should actually log at this level
   * This internal method remains unchanged
   */
  private shouldLog(level: LogLevel): boolean {
    return level >= this.currentLevel;
  }

  /**
   * Format the message with prefix and timestamp
   * This formatting remains the same to maintain consistent log appearance
   */
  private formatMessage(level: string, message: string): string {
    const timestamp = new Date().toISOString().substring(11, 23); // Just time portion
    const prefixPart = this.prefix ? `[${this.prefix}]` : "";
    return `${timestamp}${prefixPart}[${level}] ${message}`;
  }

  /**
   * TRACE: Most detailed logging - use for step-by-step debugging
   * This method signature and behavior remains exactly the same
   */
  trace(message: string, data?: any): void {
    if (!this.shouldLog(LogLevel.TRACE)) return;

    const formatted = this.formatMessage("TRACE", message);
    if (data !== undefined) {
      console.log(`%c${formatted}`, "color: #888", data);
    } else {
      console.log(`%c${formatted}`, "color: #888");
    }
  }

  /**
   * DEBUG: Detailed information for diagnosing problems
   * This method signature and behavior remains exactly the same
   */
  debug(message: string, data?: any): void {
    if (!this.shouldLog(LogLevel.DEBUG)) return;

    const formatted = this.formatMessage("DEBUG", message);
    if (data !== undefined) {
      console.log(`%c${formatted}`, "color: #0066cc", data);
    } else {
      console.log(`%c${formatted}`, "color: #0066cc");
    }
  }

  /**
   * INFO: General information about what's happening
   * This method signature and behavior remains exactly the same
   */
  info(message: string, data?: any): void {
    if (!this.shouldLog(LogLevel.INFO)) return;

    const formatted = this.formatMessage("INFO", message);
    if (data !== undefined) {
      console.info(`%c${formatted}`, "color: #00aa00", data);
    } else {
      console.info(`%c${formatted}`, "color: #00aa00");
    }
  }

  /**
   * WARN: Something unexpected but not breaking
   * This method signature and behavior remains exactly the same
   */
  warn(message: string, data?: any): void {
    if (!this.shouldLog(LogLevel.WARN)) return;

    const formatted = this.formatMessage("WARN", message);
    if (data !== undefined) {
      console.warn(`%c${formatted}`, "color: #ff8800", data);
    } else {
      console.warn(`%c${formatted}`, "color: #ff8800");
    }
  }

  /**
   * ERROR: Something went wrong
   * This method signature and behavior remains exactly the same
   */
  error(message: string, data?: any): void {
    if (!this.shouldLog(LogLevel.ERROR)) return;

    const formatted = this.formatMessage("ERROR", message);
    if (data !== undefined) {
      console.error(`%c${formatted}`, "color: #cc0000", data);
    } else {
      console.error(`%c${formatted}`, "color: #cc0000");
    }
  }

  /**
   * Change log level at runtime (useful for debugging)
   * This method remains exactly the same
   */
  setLevel(level: LogLevel): void {
    this.currentLevel = level;
  }

  /**
   * Get current log level
   * This method remains exactly the same
   */
  getLevel(): LogLevel {
    return this.currentLevel;
  }
}

/**
 * Create a logger for a specific component or module
 * This function signature and behavior remains exactly the same
 * All your existing createLogger('ComponentName') calls will continue to work
 */
export function createLogger(name: string): Logger {
  return new Logger(name);
}

/**
 * Default logger for general use
 * This remains exactly the same for backward compatibility
 */
export const logger = new Logger();

/**
 * Global function to change log level for all future loggers
 * This function remains exactly the same
 */
let globalLogLevel: LogLevel | null = null;

export function setGlobalLogLevel(level: LogLevel): void {
  globalLogLevel = level;
  logger.setLevel(level);
}

/**
 * Manual log level override specifically designed for Office Add-in testing
 * This is a new function that provides easy debugging capabilities
 */
export function setManualLogLevel(levelName: string): void {
  try {
    const level = LogLevel[levelName.toUpperCase() as keyof typeof LogLevel];
    if (level !== undefined) {
      // Store the manual override in a window property
      (window as any).__MANUAL_LOG_LEVEL__ = level;

      // Update existing logger instances
      logger.setLevel(level);
      if (globalLogLevel !== null) {
        setGlobalLogLevel(level);
      }

      console.info(`✅ Log level manually set to ${levelName.toUpperCase()}`);
    } else {
      const validLevels = Object.keys(LogLevel)
        .filter((k) => isNaN(Number(k)))
        .join(", ");
      console.error(`❌ Invalid log level: ${levelName}. Valid levels: ${validLevels}`);
    }
  } catch (error) {
    console.error("❌ Error setting manual log level:", error);
  }
}

// Add browser console helpers for all environments - enables runtime debugging anywhere
try {
  if (typeof window !== "undefined") {
    // Simple level setting function for browser console
    (window as any).setLogLevel = (levelName: string) => {
      setManualLogLevel(levelName);
    };

    // Helper to show current log level
    (window as any).getLogLevel = () => {
      const level = logger.getLevel();
      const levelName = LogLevel[level];
      console.info(`Current log level: ${levelName}`);
      return levelName;
    };

    // Helper to show all available commands
    (window as any).showLogHelp = () => {
      console.group("🛠️ Logger Controls for Office Add-ins");
      console.log('setLogLevel("INFO") - Set log level (TRACE, DEBUG, INFO, WARN, ERROR, SILENT)');
      console.log("getLogLevel() - Show current log level");
      console.log("showLogHelp() - Show this help message");
      console.groupEnd();
    };
  }
} catch (error) {
  // If browser console setup fails, continue silently
  // This ensures the logger works even if console enhancement fails
}
